import { Dispatch, useEffect, useMemo, useState } from "preact/hooks";
import { Trans } from "@lingui/react/macro";
import { TicketOdd, TotocalcioService, TotoEvent, TotoTicket, UserBet, useTotocalcioTicket, useTotocalcioTournamentsList } from "../../../services/totocalcio/totocalcio-service";
import { StatusMessage, StatusMessageButtons, StatusMessageCancel, StatusMessageText } from "../mobile-templates/ticket";

import { Fragment } from "preact/jsx-runtime";
import { ButtonOdds, TotoEventDiv, TotoEventRow, TotoEventRowWrapper, TotoEventTh, TotoOddsContainer, TotoOutcomeTd, TotoRandomPicker, TotoSettlementsPage, TournamentPreviewWidget } from "../desktop-templates/toto-jackpot";
import { OutTotoEntry, TotoBetStatus, TotoOutcomeStatus } from "../../../services/totocalcio/types";

import * as I from "../utils/icons.jsx";
import * as P from "../mobile-templates/ticket.jsx";
import MoneyFormatter from "../utils/money-formatter";
import User from "../../../services/user";



export function TotoTournamentsList({ tournaments, handleOpenTournament }: {
    tournaments: ReturnType<typeof useTotocalcioTournamentsList>,
    handleOpenTournament: (t: OutTotoEntry) => void
}) {
    return <>
        <div class="p-1">
            <div class="flex items-center px-6 py-2 bg-harif-800 border-b border-b-harif-950 space-x-2 justify-center relative">
                <span class="text-sm font-bold tracking-wide text-amber-500 uppercase"><Trans>Jackpots</Trans></span>
            </div>
        </div>
        <div class="p-3 space-y-3">
            {tournaments.filteredList.map(tournament =>
                <TournamentPreviewWidget
                    tournament={tournament}
                    onClick={() => handleOpenTournament(tournament)}
                />)}
        </div>
    </>
}

export function SingleTournament({ handleBackClick, tournament, details, ticketApi }: {
    handleBackClick: Dispatch<any>,
    tournament: OutTotoEntry,
    details: {
        tournamentInfo: Record<string, TotoEvent>,
        isLoading: boolean,
    },
    ticketApi: TotoTicket,
}) {
    return <>
        <div class="p-1">
            <div class="flex items-center px-6 py-2 bg-harif-800 border-b border-b-harif-950 space-x-2 justify-center relative">
                <span class="text-sm font-bold tracking-wide text-amber-500 uppercase">Jackpots</span>
                <div
                    class="return flex cursor-pointer absolute left-5 bg-harifzinc-700 h-5 w-5 items-center justify-center border border-harifzinc-600"
                    onClick={handleBackClick}
                >
                    <I.AnglesLeft class="h-3 w-3 text-white" />
                </div>
            </div>
        </div>
        {new Date(tournament.tournament.startDate).getTime() > Date.now()
            ? <TotoOpenTournament details={details} ticketApi={ticketApi} />
            : <TotoEndedTournament currentTournament={tournament} details={details} />
        }
    </>
}

export function useOddState(ticket: TotoTicket, oddInfo: string) {
    const [isSelected, setSelected] = useState(false);

    const handleUpdate = () => {
        setSelected(ticket.hasOdd(oddInfo));
    }

    useEffect(() => {
        handleUpdate();
        ticket.addListener(handleUpdate)
        return () => ticket.removeListener(handleUpdate)
    }, []);

    return {
        isSelected,
        toggleOdd: () => ticket.toggleOdd(oddInfo)
    }
}

export function TotoOpenTournament({ details, ticketApi }: { ticketApi: TotoTicket, details: { tournamentInfo: Record<string, TotoEvent>, isLoading: boolean } }) {
    const outcomes = useMemo(() => details.isLoading ? [] : Object.values(Object.values(Object.values(details.tournamentInfo)[0].sub)[0].sub), [details.isLoading])
    return <div class="p-1">
        <div class="overflow-hidden">
            <TotoRandomPicker ticketApi={ticketApi} />
            {!details.isLoading && <TotoEventTh>
                {outcomes.map(odd => {
                    return <TotoOutcomeTd>{odd.name}</TotoOutcomeTd>
                })}
            </TotoEventTh>}
            {!details.isLoading && Object.values(details.tournamentInfo).sort((a, b) => a.time.getTime() - b.time.getTime()).map(event =>
                <TotoEventRowWrapper>
                    <TotoEventRow>
                        <TotoEventDiv match={event} />
                        <TotoOddsContainer>
                            {Object.values(event.sub).flatMap(market => Object.values(withProbabilities(market.sub))).map(odd => {
                                const oddsInfo = useOddState(ticketApi, odd.code);
                                return <ButtonOdds odd={odd} ticketService={oddsInfo}>
                                    <span class="leading-none">{odd.name}</span>
                                    <span class="leading-none text-xxs font-light">{(odd.probability * 100).toFixed(0)} %</span>
                                </ButtonOdds>
                            })}
                        </TotoOddsContainer>
                    </TotoEventRow>
                </TotoEventRowWrapper>
            )}
        </div>
    </div>
}

export function withProbabilities(marketOdds: TotoEvent['sub'][string]['sub']) {
    const totProbs = Object.values(marketOdds).map(odd => 1 / (odd.value || 1)).reduce((a, b) => a + b, 0);
    return Object.fromEntries(Object.entries(marketOdds).map(([k, v]) => [k, { ...v, probability: 1 / ((v.value || 1) * totProbs) }]));
}

export function TotoEndedTournament({ details, currentTournament }: { details: { tournamentInfo: Record<string, TotoEvent>, isLoading: boolean }, currentTournament: OutTotoEntry }) {
    return !details.isLoading && <TotoSettlementsPage tournamentInfo={currentTournament} partite={Object.values(details.tournamentInfo)} />
}


//// Ticket

export function TotoTicketSidebar({ ticketApi, isLogged }: { ticketApi: TotoTicket | null, isLogged: boolean }) {
    const ticketState = useTotocalcioTicket(ticketApi);

    return <div class="overflow-hidden border border-harifzinc-800">
        <TotoTicketHeader oddsCount={ticketState.odds.value.length} />
        <div class="bg-harif-900 text-white">
            <TotoTicketBody isLogged={isLogged} ticketApi={ticketApi} ticketState={ticketState} />
        </div>
    </div>;
}

export function TotoTicketHeader({ oddsCount }: { oddsCount: number }) {
    return (
        <Fragment>
            <div class="flex w-full items-stretch">
                <P.BetslipButtonLarge active={true} onClick={null}>
                    <Trans>Jackpot Betslip</Trans> <P.BetslipIndicator>{oddsCount}</P.BetslipIndicator>
                </P.BetslipButtonLarge>
            </div>
        </Fragment>
    );
}

export function TotoTicketBody({ ticketApi, ticketState, isLogged }: { ticketApi: TotoTicket | null, ticketState: ReturnType<typeof useTotocalcioTicket>, isLogged: boolean }) {
    if (!ticketApi || ticketState.odds.value.length === 0) {
        return (
            <div class="empty-ticket text-center text-sm px-4 py-5 text-gray-400">
                <Trans>No bet has been selected. To select a bet, please click on the respective odds</Trans>
            </div>
        );
    }

    if (ticketState.isPlacing.value || ticketState.lastPlacingResult.value) {
        return <TotoEndedTicketPopups ticketState={ticketState} ticketApi={ticketApi} />;
    }

    return (
        <Fragment>
            <div class="flex-1 overflow-y-auto">
                <TotoTicketSelections ticketApi={ticketApi} odds={ticketState.odds.value} />
            </div>

            <TotoSelectionInterface ticketApi={ticketApi} ticketState={ticketState} />
            <TotoTicketWarnings isLogged={isLogged} />

            <P.TicketButtonsContainer>
                <div class="flex w-full justify-between items-center h-10 overflow-hidden border border-harifzinc-700">
                    <P.CancelButton onClick={() => {
                        [...ticketApi.odds].forEach(odd => ticketApi.toggleOdd(odd.code));
                    }}>
                        <span class="block"><I.ClearIcon class="h-4" /></span>
                        <span class="block"><Trans>Clear</Trans></span>
                        <span class="block"></span>
                    </P.CancelButton>

                    <P.PlaceButton
                        onClick={() => ticketApi.placeBet()}
                        disabled={!ticketState.isPlayable.value || !isLogged}
                    >
                        <span class="block"><Trans>Bet</Trans></span>
                    </P.PlaceButton>
                </div>
            </P.TicketButtonsContainer>
        </Fragment>
    );
}

export function TotoTicketSelections({ ticketApi, odds }: { ticketApi: TotoTicket, odds: TicketOdd[] }) {
    return (
        <P.BestlipOddsSection>
            {odds.map(odd => (
                <P.TicketRow closed={false} key={odd.code}>
                    <P.TicketEventHeader>
                        <P.TicketEventName>
                            <div class="truncate font-bold text-white">
                                {odd.parent.parent.name}
                            </div>
                        </P.TicketEventName>

                        <P.TicketEventRemoveBtn onClick={() => ticketApi.toggleOdd(odd.code)}>
                            <I.CloseIcon class="inline-block h-3 fill-current" />
                        </P.TicketEventRemoveBtn>
                    </P.TicketEventHeader>

                    <P.TicketEventOdd closed={false}>
                        <P.MarketContainer>
                            <span class="text-gray-400">{odd.parent.name}:</span>
                            <P.OutcomeName>{odd.name}</P.OutcomeName>
                        </P.MarketContainer>
                    </P.TicketEventOdd>
                </P.TicketRow>
            ))}
        </P.BestlipOddsSection>
    );
}

export function TotoSelectionInterface({ ticketApi, ticketState }: { ticketApi: TotoTicket, ticketState: ReturnType<typeof useTotocalcioTicket> }) {
    const betAmount = ticketState.betAmount.value;
    const isPlayable = ticketState.isPlayable.value;
    const maxPrize = isPlayable ? ticketApi.maxWin()?.displayPrize : 0;

    return (
        <Fragment>
            <P.TicketTotalStakeRow>
                <P.PaddedCell><Trans>Stake</Trans></P.PaddedCell>
                <P.PaddedCell><MoneyFormatter value={betAmount} /></P.PaddedCell>
            </P.TicketTotalStakeRow>

            {isPlayable && maxPrize > 0 ? (
                <P.RegularTicketWinPreview>
                    <P.RegularTicketWinLabel><Trans>Potential Max Win</Trans></P.RegularTicketWinLabel>
                    <P.RegularTicketWinAmountBold><MoneyFormatter value={maxPrize} /></P.RegularTicketWinAmountBold>
                </P.RegularTicketWinPreview>
            ) : (
                <P.RegularTicketWinPreview>
                    <P.RegularTicketWinLabel><Trans>Add more selections to qualify for a prize</Trans></P.RegularTicketWinLabel>
                </P.RegularTicketWinPreview>
            )}
        </Fragment>
    );
}

export function TotoTicketWarnings({ isLogged }: { isLogged: boolean }) {
    if (!isLogged)
        return <P.WarningRow>
            <P.WarningIcon /> <Trans>You need to login to be able to place a bet.</Trans>
        </P.WarningRow>;
    return null;
}

export function TotoEndedTicketPopups({ ticketState, ticketApi }: { ticketState: ReturnType<typeof useTotocalcioTicket>, ticketApi: TotoTicket }) {
    if (ticketState.isPlacing.value) {
        return (
            <StatusMessage>
                <I.HourglassSlim class="h-12 animate-spin text-amber-500" />
                <StatusMessageText><Trans>Please wait...</Trans></StatusMessageText>
            </StatusMessage>
        );
    }

    const result = ticketState.lastPlacingResult.value;
    if (!result)
        return null;

    if (result.status === 'error') {
        return (
            <StatusMessage>
                <I.CircleXmark class="h-12 text-red-500" />
                <StatusMessageText><Trans>Error</Trans></StatusMessageText>
                <StatusMessageText>{String(result.error)}</StatusMessageText>
                <StatusMessageButtons>
                    <StatusMessageCancel onClick={() => {
                        ticketApi.lastPlacingResult = null;
                        ticketApi.recalcTicket();
                    }}>
                        <Trans>Close</Trans>
                    </StatusMessageCancel>
                </StatusMessageButtons>
            </StatusMessage>
        );
    }

    if (result.status === 'success') {
        return (
            <StatusMessage>
                <I.CircleCheck class="h-12 text-green-500" />
                <StatusMessageText>
                    <h3><Trans>Your Jackpot bet is successfully accepted</Trans></h3>
                    <div class="mt-2 text-sm text-gray-400">
                        ID: {result.data.ticketId}
                    </div>
                </StatusMessageText>
                <StatusMessageButtons>
                    <StatusMessageCancel onClick={() => {
                        [...ticketApi.odds].forEach(odd => ticketApi.toggleOdd(odd.code));
                        ticketApi.lastPlacingResult = null;
                        ticketApi.recalcTicket();
                    }}>
                        <Trans>Continue</Trans>
                    </StatusMessageCancel>
                </StatusMessageButtons>
            </StatusMessage>
        );
    }

    return null;
}


export function useMyJackpots(api: TotocalcioService) {
    const [bets, setBets] = useState<UserBet[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        setIsLoading(true);
        api.getMyBets()
            .then(setBets)
            .catch(setError)
            .finally(() => setIsLoading(false));
    }, [api]);

    return { bets, isLoading, error };
}

// --- INDICATORE DI STATO ---
export function StatusCircle({ status }: { status: TotoBetStatus | TotoOutcomeStatus }) {
    let bgColor = "bg-harifzinc-600";
    if (status === TotoBetStatus.WIN || status === TotoOutcomeStatus.WIN) {
        bgColor = "bg-green-600";
    } else if (status === TotoBetStatus.LOSE || status === TotoOutcomeStatus.LOSE) {
        bgColor = "bg-red-600";
    }
    return <div class={`w-3 h-3 ${bgColor} shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)] shrink-0`} />;
}

// --- CARD DELLA SCHEDINA ---
export function JackpotBetCard({ betData }: { betData: UserBet }) {
    const [isOpen, setIsOpen] = useState(false);
    const { bet, selections } = betData;
    const isWin = bet.status === TotoBetStatus.WIN;

    return (
        <div class="mb-2 border border-harifzinc-800 bg-harif-900 font-sans overflow-hidden">
            {/* HEADER */}
            <div class="cursor-pointer select-none" onClick={() => setIsOpen(!isOpen)}>

                {/* Fascia 1: ID e Stato */}
                <div class="bg-harifzinc-800 border-b border-b-harif-950 px-2 py-1 flex justify-between items-center">
                    <div class="text-sm font-black flex items-center gap-2 text-amber-500">
                        <span>ID: {bet.id}</span>
                        <span class="font-normal text-gray-400">
                            {new Date(bet.time).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        </span>
                    </div>
                    <div class="flex items-center gap-2">
                        <StatusCircle status={bet.status} />
                        <svg class={`w-3 h-3 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                {/* Fascia 2: Info Torneo e Soldi */}
                <div class="bg-harif-800 px-2 py-1 grid grid-cols-2 items-center text-sm border-b border-b-harif-950">
                    <div class="truncate font-medium italic text-gray-300">
                        Jackpot #{bet.tournamentId}
                    </div>
                    <div class="flex justify-end gap-3 font-bold text-white">
                        <div class="flex gap-1">
                            <span class="text-gray-400 font-normal"><Trans>Stake</Trans>:</span>
                            <MoneyFormatter value={bet.stake} currency={bet.currency as string} />
                        </div>
                        {isWin && (
                            <div class="flex gap-1 text-green-400">
                                <span class="text-gray-400 font-normal"><Trans>Win Amount</Trans>:</span>
                                <MoneyFormatter value={bet.win} currency={bet.currency as string} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* LISTA EVENTI */}
            {isOpen && (
                <div class="bg-harif-900 border-t border-t-harif-800 flex flex-col">
                    {selections.map((sel, idx) => {
                        const matchDate = new Date(sel.event?.time);
                        const timeStr = matchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const dateStr = matchDate.toLocaleDateString([], { day: '2-digit', month: 'short' });

                        return (
                            <div
                                key={idx}
                                class="flex items-center px-2 py-1 border-b border-b-harif-950 last:border-0 odd:bg-harif-900 even:bg-harif-800 text-sm sm:text-xs"
                            >
                                {/* Data/Ora */}
                                <div class="w-8 sm:w-16 text-gray-500 shrink-0 leading-tight text-center">
                                    <div class="text-xs uppercase leading-none">{dateStr}</div>
                                    <div class="font-mono">{timeStr}</div>
                                </div>

                                {/* Squadre */}
                                <div class="flex-1 font-bold text-white whitespace-nowrap overflow-hidden truncate px-1.5 min-w-0">
                                    <span class="truncate">{sel.event?.home} - {sel.event?.away}</span>
                                </div>

                                {/* Pronostico */}
                                <div class="w-12 sm:w-12 text-center font-black text-xs text-amber-500 shrink-0 uppercase truncate">
                                    {sel.outcomeName}
                                </div>

                                {/* Pallino di stato */}
                                <div class="w-4 flex justify-end shrink-0 pl-1">
                                    <StatusCircle status={sel.status} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// --- CONTAINER DELLA PAGINA ---
export function MyJackpotsView({ api }: { api: TotocalcioService }) {
    const { bets, isLoading, error } = useMyJackpots(api);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { paginatedBets, totalPages } = useMemo(() => {
        if (!bets) return { paginatedBets: [], totalPages: 0 };
        const sortedBets = [...bets].sort((a, b) => new Date(b.bet.time).getTime() - new Date(a.bet.time).getTime());
        const total = Math.ceil(sortedBets.length / itemsPerPage);
        const sliced = sortedBets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
        return { paginatedBets: sliced, totalPages: total };
    }, [bets, currentPage]);

    if (isLoading) {
        return <div class="p-4 text-center text-gray-400 text-sm font-bold bg-harif-950 h-full"><Trans>Loading History...</Trans></div>;
    }

    if (error) {
        return <div class="p-4 text-center text-red-500 text-sm font-bold bg-harif-950 h-full"><Trans>Error loading bets.</Trans></div>;
    }

    if (bets.length === 0) {
        return <div class="p-4 text-center text-gray-400 text-sm bg-harif-950 h-full"><Trans>No records found.</Trans></div>;
    }

    return (
        <div class="bg-harif-950 min-h-full p-2 sm:p-4">
            <div class="max-w-3xl mx-auto w-full pb-8">

                {paginatedBets.map(betData => (
                    <JackpotBetCard key={betData.bet.id} betData={betData} />
                ))}

                {/* Paginazione */}
                {totalPages > 1 && (
                    <div class="flex justify-center mt-4">
                        <div class="flex border border-harifzinc-800 text-xs font-bold">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                class="bg-harif-900 text-gray-300 px-4 py-2 hover:bg-harif-800 disabled:bg-harif-950 disabled:text-gray-600 border-r border-harifzinc-800"
                            >
                                <Trans>Previous</Trans>
                            </button>

                            <div class="bg-harifzinc-800 text-amber-500 px-4 py-2 border-r border-harifzinc-800">
                                {currentPage}
                            </div>

                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                class="bg-harif-900 text-gray-300 px-4 py-2 hover:bg-harif-800 disabled:bg-harif-950 disabled:text-gray-600"
                            >
                                <Trans>Next</Trans>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}